import { Card, Col, Row, Skeleton, Typography, Result } from "antd";
import axios from "axios";
import { Event, SharedQuota, StudyProgramQuota } from "@bindeleddet/core/types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import colors from "constants/colors";
import StudyProgramQuotaStep from "./Steps/StudyProgramQuota";
import TotalSeatsStep from "./Steps/TotalSeats";

const EventSeats = () => {
  const { Title, Paragraph, Text } = Typography;
  const [event, setEvent] = useState<Event>();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [sharedQuotas, setSharedQuotas] = useState<SharedQuota[]>([]);
  const [studyProgramQuotas, setStudyProgramQuotas] = useState<StudyProgramQuota[]>([]);
  const [currentSharedQuota, setCurrentSharedQuota] = useState<SharedQuota | undefined>();
  const [registeredStudents, setRegisteredStudents] = useState<number>();

  useEffect(() => {
    setLoading(true);
    axios
      .get<Event>(`/events/admin-events/${id}/`)
      .then(response => {
        setEvent(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    axios.get(`/events/${id}/registrations/`).then(response => {
      setRegisteredStudents(response.data.length);
    });
  }, [id]);

  useEffect(() => {
    axios.get<SharedQuota[]>(`/events/shared-quotas/`).then(response => {
      setSharedQuotas(response.data.filter(quota => quota.event === Number(id)));
    });
    axios.get<StudyProgramQuota[]>(`/events/${id}/study-program-quotas/`).then(response => {
      setStudyProgramQuotas(response.data);
    });
  }, [id]);

  const handleQuotas = (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => {
    setSharedQuotas(sq);
    setStudyProgramQuotas(spq);
    setCurrentSharedQuota(csq);
  };

  if (!event && !loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Result title="Fant ikke arrangementet" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />
      </div>
    );
  return (
    <>
      <Title>Fordel plasser</Title>
      {loading || !event ? (
        <Skeleton />
      ) : (
        <Row gutter={[20, 20]}>
          <Col lg={18} md={24} xs={24}>
            <StudyProgramQuotaStep
              sharedQuotas={sharedQuotas}
              studyProgramQuotas={studyProgramQuotas}
              handleQuotas={handleQuotas}
              eventId={Number(id)}
            />
            <TotalSeatsStep
              studyProgramQuotas={studyProgramQuotas}
              sharedQuotas={sharedQuotas}
              currentSharedQuota={currentSharedQuota}
              handleQuotas={handleQuotas}
            />
          </Col>
          <Col lg={6} md={24} xs={24}>
            <Card title="Totalt" style={{ marginBottom: "20px" }}>
              <Row>
                <Col span={12}>
                  <Title style={{ color: colors.primary, margin: "0" }}>
                    {sharedQuotas.reduce((total, quota) => total + quota.max_students_allowed, 0)}
                  </Title>
                  <Text style={{ color: colors.secondary }}>PLASSER</Text>
                </Col>
                <Col span={12}>
                  <Title style={{ color: colors.primary, margin: "0" }}>
                    {registeredStudents !== undefined ? registeredStudents : <Skeleton.Button active size="large" />}
                  </Title>
                  <Text style={{ color: colors.secondary }}>PÅMELDTE</Text>
                </Col>
              </Row>
            </Card>
            <Card title="Forklaringer">
              <Title level={5}>1. Linje og trinn</Title>
              <Paragraph>
                Velg en studielinje og de aktuelle trinnene, og angi maksimalt antall studenter for denne gruppen. For
                eksempel kan du velge å tillate opptil 10 studenter fra førsteår Indøk.
              </Paragraph>
              <Paragraph>
                Hvis du ønsker å inkludere linjen i en eksisterende kvote, velg kvoten i nedtrekksmenyen i steg 1 og
                trykk <Text italic>Legg til</Text>.
              </Paragraph>
              <Paragraph>
                Hvis du ønsker å opprette en ny kvote, velg <Text italic>Ny kvote</Text> i nedtrekksmenyen og trykk{" "}
                <Text italic>Legg til</Text>.
              </Paragraph>
              <Title level={5}>2. Kvoter (Delte plasser)</Title>
              <Paragraph>
                Angi hvor mange plasser som kan deles mellom ulike linjer og trinn i samme kvote. For eksempel kan du
                sette at førsteår Indøk og førsteår Data totalt kan dele på 10 plasser.
              </Paragraph>
              <Paragraph>
                Ønsker du å endre noe med kvoten eller hver enkelt linje, kan du trykke på <Text italic>Endre</Text> på
                kvoten. Her kan du tilpasse antall plasser totalt for kvoten, antall plasser for hver linje, og hvilke
                trinn som får være med ved å trykke direkte på sirklene med trinnene. Du kan også fjerne linjer ved å
                trykke på søppelkassen. Ingenting blir lagret før du trykker <Text italic>Lagre</Text>.
              </Paragraph>
              <Paragraph italic>
                (Merk: Det er antall plasser totalt i kvoten som gjelder, ikke summen av plassene per linje. For
                eksempel: Hvis førsteår Indøk og førsteår Data hver har satt 10 plasser, men de deler en kvote på 10
                plasser totalt, så er det kun 10 plasser tilgjengelig for begge linjene sammen. Hvis de 10 første som
                melder seg på er fra førsteår Indøk, vil det ikke være plasser igjen for førsteår Data, selv om de har
                satt 10 plasser.)
              </Paragraph>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default EventSeats;
